<script setup lang="ts">
import type { ICard } from '~/components/entity/card'
import { ContentBlock } from '~/components/ui/content-block'
import { LucideDownload } from 'lucide-vue-next'

const props = defineProps<{ card: ICard }>()
</script>
<template>
  <section>
    <ContentBlock
      variant="white"
      heading-class="h4"
      heading-tag="h2"
      :heading-text="$t('widget.card_page.terms_of_use.heading')"
    >
      <Content v-if="props.card.issue_terms">
        <div v-hydrated-html="props.card.issue_terms" />
      </Content>
      <div class="hidden md:block">
        <table class="w-full">
          <thead class="text-left">
            <tr>
              <th class="border-b px-3 py-4">
                <Heading tag="h3" class="h6">{{
                  $t(
                    `widget.card_page.terms_of_use.currency_type.${props.card.currency_type == 'multi' ? 'multi' : 'single'}`
                  )
                }}</Heading>
              </th>
              <th v-for="item in props.card.rates" :key="item.id" class="border-b px-3 py-4">
                <div class="flex items-center justify-center gap-1">
                  <div class="icon"><UiIcon :name="`currency_${item.currency_id}`" /></div>
                  <div class="font-normal">
                    {{ useCurrencyProp(item.currency_id, 'code') }}
                  </div>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="props.card.rates?.some((i) => i.daily_limit)">
              <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                {{ $t('widget.card_page.terms_of_use.daily_limit') }}
              </td>
              <td
                v-for="item in props.card.rates"
                :key="item.id"
                class="border-b border-gray-200 px-3 py-4 text-center"
              >
                {{
                  Number(item.daily_limit) > 0
                    ? useCurrencyFormat(item.daily_limit, item.currency_id)
                    : '-'
                }}
              </td>
            </tr>

            <tr v-if="props.card.rates?.some((i) => i.monthly_limit)">
              <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                {{ $t('widget.card_page.terms_of_use.monthly_limit') }}
              </td>
              <td
                v-for="item in props.card.rates"
                :key="item.id"
                class="border-b border-gray-200 px-3 py-4 text-center"
              >
                {{
                  Number(item.monthly_limit) > 0
                    ? useCurrencyFormat(item.monthly_limit, item.currency_id)
                    : '-'
                }}
              </td>
            </tr>

            <template v-if="props.card.option_is_cash_withdrawals !== false">
              <tr>
                <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                  {{ $t('widget.card_page.terms_of_use.own_atm_withdrawal_fee') }}
                </td>
                <td
                  v-for="item in props.card.rates"
                  :key="item.id"
                  class="border-b border-gray-200 px-3 py-4 text-center"
                >
                  {{
                    Number(item.own_atm_withdrawal_fee) > 0
                      ? item.own_atm_withdrawal_fee + '%'
                      : $t('widget.card_page.terms_of_use.free')
                  }}
                </td>
              </tr>

              <tr>
                <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                  {{ $t('widget.card_page.terms_of_use.partner_atm_withdrawal_fee') }}
                </td>
                <td
                  v-for="item in props.card.rates"
                  :key="item.id"
                  class="border-b border-gray-200 px-3 py-4 text-center"
                >
                  {{
                    Number(item.partner_atm_withdrawal_fee) > 0
                      ? item.partner_atm_withdrawal_fee + '%'
                      : $t('widget.card_page.terms_of_use.free')
                  }}
                </td>
              </tr>

              <tr>
                <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                  {{ $t('widget.card_page.terms_of_use.foreign_atm_withdrawal_fee') }}
                </td>
                <td
                  v-for="item in props.card.rates"
                  :key="item.id"
                  class="border-b border-gray-200 px-3 py-4 text-center"
                >
                  {{
                    Number(item.foreign_atm_withdrawal_fee) > 0
                      ? item.foreign_atm_withdrawal_fee + '%'
                      : $t('widget.card_page.terms_of_use.free')
                  }}
                </td>
              </tr>
            </template>

            <tr>
              <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                {{ $t('widget.card_page.terms_of_use.issue_fee') }}
              </td>
              <td
                v-for="item in props.card.rates"
                :key="item.id"
                class="border-b border-gray-200 px-3 py-4 text-center"
              >
                {{
                  Number(item.issue_fee) > 0
                    ? useCurrencyFormat(item.issue_fee, item.currency_id)
                    : $t('widget.card_page.terms_of_use.free')
                }}
              </td>
            </tr>

            <tr>
              <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                {{ $t('widget.card_page.terms_of_use.service_fee') }}
              </td>
              <td
                v-for="item in props.card.rates"
                :key="item.id"
                class="border-b border-gray-200 px-3 py-4 text-center"
              >
                {{
                  Number(item.service_fee) > 0
                    ? useCurrencyFormat(item.service_fee, item.currency_id)
                    : $t('widget.card_page.terms_of_use.free')
                }}
              </td>
            </tr>

            <tr>
              <td class="border-b border-gray-200 px-3 py-4 text-gray-500">
                {{ $t('widget.card_page.terms_of_use.service_fee_yearly') }}
              </td>
              <td
                v-for="item in props.card.rates"
                :key="item.id"
                class="border-b border-gray-200 px-3 py-4 text-center"
              >
                {{
                  Number(item.service_fee_yearly) > 0
                    ? useCurrencyFormat(item.service_fee_yearly, item.currency_id)
                    : $t('widget.card_page.terms_of_use.free')
                }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex flex-col gap-4 md:hidden">
        <div
          v-for="item in props.card.rates"
          :key="item.id"
          class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div class="mb-2 text-base font-semibold">
            {{ useCurrencyProp(item.currency_id, 'title') }}
          </div>
          <div class="text-muted-foreground space-y-1 text-sm">
            <div>
              <span class="text-foreground font-medium"
                >{{ $t('widget.card_page.terms_of_use.daily_limit') }}:</span
              >
              {{
                Number(item.daily_limit) > 0
                  ? useCurrencyFormat(item.daily_limit, item.currency_id)
                  : '-'
              }}
            </div>
            <div>
              <span class="text-foreground font-medium"
                >{{ $t('widget.card_page.terms_of_use.monthly_limit') }}:</span
              >
              {{
                Number(item.monthly_limit) > 0
                  ? useCurrencyFormat(item.monthly_limit, item.currency_id)
                  : '-'
              }}
            </div>
            <template v-if="props.card.option_is_cash_withdrawals !== false">
              <div>
                <span class="text-foreground font-medium"
                  >{{ $t('widget.card_page.terms_of_use.own_atm_withdrawal_fee') }}:</span
                >
                {{
                  Number(item.own_atm_withdrawal_fee) > 0
                    ? item.own_atm_withdrawal_fee + '%'
                    : $t('widget.card_page.terms_of_use.free')
                }}
              </div>
              <div>
                <span class="text-foreground font-medium"
                  >{{ $t('widget.card_page.terms_of_use.partner_atm_withdrawal_fee') }}:</span
                >
                {{
                  Number(item.partner_atm_withdrawal_fee) > 0
                    ? item.partner_atm_withdrawal_fee + '%'
                    : $t('widget.card_page.terms_of_use.free')
                }}
              </div>
              <div>
                <span class="text-foreground font-medium"
                  >{{ $t('widget.card_page.terms_of_use.foreign_atm_withdrawal_fee') }}:</span
                >
                {{
                  Number(item.foreign_atm_withdrawal_fee) > 0
                    ? item.foreign_atm_withdrawal_fee + '%'
                    : $t('widget.card_page.terms_of_use.free')
                }}
              </div>
            </template>
            <div>
              <span class="text-foreground font-medium"
                >{{ $t('widget.card_page.terms_of_use.issue_fee') }}:</span
              >
              {{
                Number(item.issue_fee) > 0
                  ? useCurrencyFormat(item.issue_fee, 1)
                  : $t('widget.card_page.terms_of_use.free')
              }}
            </div>
            <div>
              <span class="text-foreground font-medium"
                >{{ $t('widget.card_page.terms_of_use.service_fee') }}:</span
              >
              {{
                Number(item.service_fee) > 0
                  ? useCurrencyFormat(item.service_fee, 1)
                  : $t('widget.card_page.terms_of_use.free')
              }}
            </div>
            <div>
              <span class="text-foreground font-medium"
                >{{ $t('widget.card_page.terms_of_use.service_fee_yearly') }}:</span
              >
              {{
                Number(item.service_fee_yearly) > 0
                  ? useCurrencyFormat(item.service_fee_yearly, 1)
                  : $t('widget.card_page.terms_of_use.free')
              }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="props.card.tariffs_file" class="mt-8">
        <Button as-child variant="outline-blue">
          <NuxtLinkLocale :to="props.card.tariffs_file" target="_blank">
            <LucideDownload />{{ $t('widget.card_page.terms_of_use.download_pdf') }}
          </NuxtLinkLocale>
        </Button>
      </div>

      <Content v-if="props.card.tariffs">
        <Heading tag="h2" class="h5 mt-8">{{
          $t('widget.card_page.terms_of_use.tariffs_heading')
        }}</Heading>
        <div v-hydrated-html="props.card.tariffs" />
      </Content>
    </ContentBlock>
  </section>
</template>
